/**
 * Presentation decisions shared by both event clients.
 *
 * The legacy API and the fundraising API return different shapes, but the site
 * must look identical whichever one serves the read — that is the entire premise
 * of the `VITE_FUNDRAISING_API_BASE_URL` switch. Anything that turns backend
 * data into what the user sees therefore lives here once, rather than being
 * reimplemented per client and drifting.
 */

import ChelseaGolfing from 'assets/chelseaGolfing.png';
import CashBash2026 from 'assets/events/2026-cash-bash.jpg';
import Bowloween2022 from 'assets/events/bowloween-2022.svg';
import Bowloween from 'assets/events/bowloween.svg';
import ComedyShow from 'assets/events/comedy-show.jpg';
import GolfOuting from 'assets/events/golf-outing.jpg';

export interface AddressParts {
    address_line_1: string;
    address_line_2?: string | null;
    city: string;
    state: string;
    zipcode: string;
}

// Keyed by the event's name, exactly. A name with no entry renders no card
// image, silently — which is how the 2026 Golf Outing, the next event to take
// registrations, went without one until 2026-08-30. Adding an event means
// adding a line here, and nothing enforces that.
//
// '2026 Cash Bash' below names no event in the database. Its 3.7 MB image is
// bundled regardless, and is the single largest asset in the build.
const EventImageMap: Record<string, string> = {
    'Golf Outing': GolfOuting,
    '2023 Golf Outing': GolfOuting,
    '2024 Golf Outing': GolfOuting,
    '2025 Golf Outing': GolfOuting,
    '2026 Golf Outing': GolfOuting,
    'Bowloween Event': Bowloween2022,
    '2023 Bowloween': Bowloween,
    '2024 Bowloween': Bowloween,
    'Comedy Show': ComedyShow,
    '2025 Comedy Show': ComedyShow,
    '2026 Cash Bash': CashBash2026,
};

const FormImageMap: Record<string, string> = {
    'Golf Outing': ChelseaGolfing,
};

/** Banner image for an event, keyed by its name. Undefined is a valid answer. */
export const imageForEvent = (eventName: string): string | undefined => EventImageMap[eventName];

/** Registration-form image for an event, keyed by its name. */
export const formImageForEvent = (eventName: string): string | undefined => FormImageMap[eventName];

export const formatAddress = (address: AddressParts): string =>
    `${address.address_line_1}${address.address_line_2 ? ` ${address.address_line_2}` : ''}, ` +
    `${address.city}, ${address.state} ${address.zipcode}`;
